import React from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import useSlug from '../utils/router/useSlug';
import postPath from '../utils/url/postPath';
import QuillContext from '../editor/QuillContext';
import {
  useVersionChangesContext,
  VersionChanges,
  VersionChangesContent,
  VersionChangesTopBar,
  VersionChangesWrapper,
} from './history/VersionChanges';
import ResponsiveDate from './history/ResponsiveDate';
import PostVerifyFooter from './PostVerifyFooter';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const POST_VERIFY_VIEW_QUERY = gql(`
  query PostVerifyView($postId: ID!) {
    post(id: $postId) {
      id
      isEditing
      # This query doesn't have Apollo cache enabled.
      # eslint-disable-next-line slab-graphql/graphql-no-corrupt-post-content
      version
      verification {
        id
      }
      verificationEntries {
        id
        version
        verifiedAt
      }
      ...VersionChangesWrapper_post
      ...PostVerifyFooter_post
    }
  }
`);

const EmptyVersionChangesNavigate = () => {
  const { allVersionChanges, postId, versionFrom, versionTo } =
    useVersionChangesContext();

  if (
    (allVersionChanges && allVersionChanges.length === 0) ||
    versionFrom === versionTo
  ) {
    return (
      <Navigate
        to={postPath({ id: postId, title: '' })}
        replace
        state={{ verification: true }}
      />
    );
  }

  return null;
};

interface PostVerifyViewInnerProps {
  versionFrom: number;
  versionTo: number;
  post: DocumentType<typeof POST_VERIFY_VIEW_QUERY>['post'];
}

const PostVerifyViewInner = ({
  versionFrom,
  versionTo,
  post,
}: PostVerifyViewInnerProps) => {
  const verificationEntry = _.first(post?.verificationEntries);

  return (
    <VersionChanges
      postId={post.id}
      versionFrom={versionFrom}
      versionTo={versionTo}
    >
      <EmptyVersionChangesNavigate />
      <VersionChangesWrapper
        initialTitle="Verify Post - Slab"
        post={post}
        topbar={
          <VersionChangesTopBar
            versionMeta={
              verificationEntry && (
                <ResponsiveDate
                  date={verificationEntry.verifiedAt}
                  render={({ condensed, dateWithPreposition }) => {
                    return condensed
                      ? `Verified ${dateWithPreposition}`
                      : `Post last verified ${dateWithPreposition}`;
                  }}
                />
              )
            }
          />
        }
      >
        <VersionChangesContent />
        {post && <PostVerifyFooter post={post} />}
      </VersionChangesWrapper>
    </VersionChanges>
  );
};

const PostVerifyView = () => {
  const [postId] = useSlug('postSlug');

  const { data, loading } = useQuery(POST_VERIFY_VIEW_QUERY, {
    variables: { postId },
    fetchPolicy: 'no-cache',
  });

  if (loading) return null;

  const post = data?.post;
  const currentVerificationEntry = _.first(post?.verificationEntries);
  const verification = post?.verification;

  // The post will show the state bar if the user has been requested as a verifier
  if (
    post == null ||
    currentVerificationEntry == null ||
    verification == null
  ) {
    return (
      <Navigate
        to={postPath({ id: postId, title: '' })}
        replace
        state={{ verification: true }}
      />
    );
  }

  return (
    <QuillContext>
      <PostVerifyViewInner
        versionFrom={currentVerificationEntry.version}
        versionTo={post.version}
        post={post}
      />
    </QuillContext>
  );
};

export default PostVerifyView;
